/**
 * Approval Center (S13 / K8 — the highest-leverage human surface).
 *
 * The single place a person answers "what is parked on my yes/no?" It lists the
 * pending two-party approval requests opened by the access/approval runtime (S7),
 * shows each request's two-party progress (votes / threshold, distinct-approver,
 * human-only), and lets an authorized human approve or reject.
 *
 * Lens/projection doctrine (technical/06, `cards-propose-runtime-executes`): this
 * screen is a READ projection of authorized data + a thin authority surface. It
 * does NOT mutate the parked effect itself. Approving calls
 *   POST /access/runtime/approvals/:id/decisions
 * and the SERVER enforces the count/distinct/human-only rules and idempotently
 * resumes the parked effect. The UI only reflects the returned post-decision
 * state (e.g. `executed` once the threshold was met and the effect resumed).
 *
 * Backend note: `GET /access/runtime/approvals` is now the read projection for
 * the pending list. This screen still degrades to a truthful proposed state if a
 * downgraded backend returns 404/405 for that endpoint.
 */
import * as React from "react";

import type { ApprovalMode, ApprovalRequest } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { Textarea } from "@/components/ui/textarea";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection } from "@/components/inspector";
import { ApiError, api } from "@/lib/api";
import { formatRelative, humanize } from "@/lib/format";

/** Shape returned by the (proposed) pending-approvals read projection. */
interface PendingApprovalsResponse {
  items: ApprovalRequest[];
}

/** Post-decision packet shape returned by the access-runtime decision endpoint. */
interface ApprovalDecisionResult {
  approval_request_id: string;
  decision: "approved" | "rejected" | "cancelled";
  request_status: ApprovalRequest["status"];
  required_approver_count: number;
  approver_count: number;
  requires_distinct_approver: boolean;
  approval_mode: ApprovalMode;
  resumed: boolean;
  already_executed: boolean;
}

function usePendingApprovals() {
  const [data, setData] = React.useState<ApprovalRequest[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  /** Distinguishes "endpoint not built" from "built but errored" (§proposed). */
  const [proposed, setProposed] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((v) => v + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProposed(false);
    api
      .get<PendingApprovalsResponse>("/access/runtime/approvals", {
        query: { status: "pending" },
      })
      .then((res) => {
        if (!cancelled) setData(res.items ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 404/405 means the read projection is not built yet → proposed.
        if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
          setData([]);
          setProposed(true);
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load approvals.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return { data, loading, error, proposed, reload };
}

function ProgressChips({ request }: { request: ApprovalRequest }) {
  const human = request.approval_mode === "human_only";
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Badge variant="secondary">
        <Icon name="approve" aria-hidden />
        {request.required_approver_count} approval
        {request.required_approver_count === 1 ? "" : "s"} required
      </Badge>
      {request.requires_distinct_approver === 1 ? (
        <Badge variant="outline">
          <Icon name="users" aria-hidden />
          Distinct approver
        </Badge>
      ) : null}
      {human ? (
        <Badge variant="warning">
          <Icon name="user" aria-hidden />
          Human only
        </Badge>
      ) : null}
      <Badge variant="outline">{humanize(request.approval_mode)}</Badge>
    </div>
  );
}

function ApprovalRequestCard({
  request,
  onDecided,
}: {
  request: ApprovalRequest;
  onDecided: (result: ApprovalDecisionResult) => void;
}) {
  const [rationale, setRationale] = React.useState("");
  const [busy, setBusy] = React.useState<"approved" | "rejected" | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<ApprovalDecisionResult | null>(null);

  const decide = React.useCallback(
    async (decision: "approved" | "rejected") => {
      setBusy(decision);
      setError(null);
      try {
        const res = await api.post<ApprovalDecisionResult>(
          `/access/runtime/approvals/${encodeURIComponent(request.id)}/decisions`,
          { decision, ...(rationale.trim() ? { rationale: rationale.trim() } : {}) },
        );
        setResult(res);
        onDecided(res);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Decision failed.");
      } finally {
        setBusy(null);
      }
    },
    [request.id, rationale, onDecided],
  );

  const resumed = result?.resumed || result?.already_executed;

  return (
    <Card data-testid="approval-request-card">
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base [overflow-wrap:anywhere]">
              {humanize(request.capability)}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground [overflow-wrap:anywhere]">
              {humanize(request.source_type)}{" "}
              <span className="font-mono text-xs">{request.source_id}</span>
              {" · "}requested {formatRelative(request.created_at)}
            </p>
          </div>
          <StatusBadge status={result?.request_status ?? request.status} />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-4 pt-0">
        <ProgressChips request={result ? { ...request, approval_mode: result.approval_mode } : request} />
        <FieldGrid>
          <Field label="Capability">
            <span className="font-mono text-xs">{request.capability}</span>
          </Field>
          <Field label="Context">
            {request.context_id ? (
              <span className="font-mono text-xs">{request.context_id}</span>
            ) : (
              "—"
            )}
          </Field>
          <Field label="Resource">
            {request.resource_type ? (
              <span className="font-mono text-xs">
                {request.resource_type}:{request.resource_id ?? "—"}
              </span>
            ) : (
              "—"
            )}
          </Field>
          <Field label="Requested by">
            <span className="font-mono text-xs">{request.requested_by_actor_id}</span>
          </Field>
        </FieldGrid>

        {result ? (
          <div
            data-testid="approval-decision-result"
            className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
          >
            <span className="font-medium">{humanize(result.decision)}</span> ·{" "}
            {result.approver_count}/{result.required_approver_count} votes ·{" "}
            {resumed ? "parked effect resumed" : "awaiting further approvals"}
          </div>
        ) : (
          <>
            <Textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Optional rationale recorded on the decision…"
              rows={2}
              aria-label="Decision rationale"
            />
            {error ? (
              <RenderState state="error" errorMessage={error} />
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => void decide("approved")}
                disabled={busy !== null}
                data-testid="approval-approve"
              >
                <Icon name="approve" data-icon="inline-start" aria-hidden />
                {busy === "approved" ? "Approving…" : "Approve"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void decide("rejected")}
                disabled={busy !== null}
                data-testid="approval-reject"
              >
                <Icon name="reject" data-icon="inline-start" aria-hidden />
                {busy === "rejected" ? "Rejecting…" : "Reject"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function ApprovalCenter() {
  const { data, loading, error, proposed, reload } = usePendingApprovals();

  // Locally drop a request once decided so the queue reflects the action without
  // a full refetch (the server is the source of truth on next reload).
  const [decided, setDecided] = React.useState<Set<string>>(new Set());
  const onDecided = React.useCallback((result: ApprovalDecisionResult) => {
    if (result.request_status !== "pending") {
      setDecided((prev) => new Set(prev).add(result.approval_request_id));
    }
  }, []);

  const visible = (data ?? []).filter((r) => !decided.has(r.id));

  const state: RenderStateKind = loading
    ? "loading"
    : error
      ? "error"
      : visible.length === 0
        ? "empty"
        : "normal";

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Approval Center"
          subtitle="Pending two-party approval gates. A decision is enforced and resumed by the access runtime — this surface never mutates the parked effect directly."
          icon="approve"
          actions={
            <Button variant="outline" size="sm" onClick={reload}>
              <Icon name="reopen" data-icon="inline-start" aria-hidden />
              Refresh
            </Button>
          }
        />

        <InspectorSection title="Pending approvals">
          <RenderState
            state={state}
            loadingRows={4}
            error={error}
            errorMessage={error ?? undefined}
            onRetry={reload}
            empty={{
              icon: "approve",
              title: proposed
                ? "Pending-approvals projection not built yet"
                : "Nothing is waiting on your approval",
              hint: proposed
                ? "The decision endpoint is live; the read projection (GET /access/runtime/approvals) ships in a follow-up slice."
                : "Approval gates opened by the access runtime will appear here for a yes/no decision.",
            }}
          >
            <div className="flex flex-col gap-3">
              {visible.map((request) => (
                <ApprovalRequestCard
                  key={request.id}
                  request={request}
                  onDecided={onDecided}
                />
              ))}
            </div>
          </RenderState>
        </InspectorSection>
      </div>
    </div>
  );
}

export default ApprovalCenter;
